import { Country } from '@resurs-internal/mui/assets/countries';
import { type AvailableCountry } from 'models/models';
import { createContext, type ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { defaultLanguageForCountry, getCountry } from '@/helpers';

export type CountryProviderProps = {
  children: ReactNode;
};

export const CountryContext = createContext<{
  country: AvailableCountry;
  matchesCountry: (...allowedCountries: AvailableCountry[]) => boolean;
  RenderForCountry: ({
    children,
    countries,
  }: {
    children: ReactNode;
    countries: AvailableCountry[];
  }) => ReactNode;
  setCountry: (country: AvailableCountry) => void;
}>({
  country: Country.NO,
  matchesCountry: () => false,
  RenderForCountry: () => undefined,
  setCountry: () => {},
});

export const CountryProvider = ({ children }: CountryProviderProps) => {
  const { i18n } = useTranslation();
  const [country, setCountry] = useState<AvailableCountry>(getCountry());

  useEffect(() => {
    i18n.changeLanguage(defaultLanguageForCountry(country));
  }, [country, i18n]);

  const matchesCountry = (...allowedCountries: AvailableCountry[]) => {
    return !!(country && allowedCountries.includes(country));
  };

  const RenderForCountry = ({
    children,
    countries,
  }: {
    children: ReactNode;
    countries: AvailableCountry[];
  }) => {
    if (matchesCountry(...countries)) return children;
  };

  return (
    <CountryContext.Provider
      value={{
        country,
        matchesCountry,
        RenderForCountry,
        setCountry,
      }}
    >
      {children}
    </CountryContext.Provider>
  );
};
